import { useState, useRef, useEffect, forwardRef, useImperativeHandle, createElement } from 'react';
import { motion } from 'framer-motion';
import { FiMessageSquare, FiX, FiSend } from 'react-icons/fi';
import AnimatePresenceWrapper from '../common/AnimatePresenceWrapper';
import { executeAgentAction } from '../../services/aiService';

// Centralisation des constantes et utilitaires
const WELCOME_MESSAGE_CONTENT = "Bonjour ! Je suis Omar Bot. Que puis je faire pour vous ?";
const createWelcomeMessage = () => ({
  content: WELCOME_MESSAGE_CONTENT,
  isBot: true,
  timestamp: new Date(),
});

const POSITIVE_CONFIRMATION_REGEX = /\b(oui|ok|d'accord|vas[-\s]?y|go|let's go|yes|yeah|bien sûr|parfait|super|top)\b/i;
const NEGATIVE_CONFIRMATION_REGEX = /\b(non|pas maintenant|plus tard|pas encore|non merci|non merci beaucoup)\b/i;

export interface Message {
  content: string;
  isBot: boolean;
  timestamp: Date;
  pendingAction?: {
    tool: string;
    params: any;
  };
}

export interface ChatBotRef {
  open: () => void;
  close: () => void;
}

const quickQuestions = [
  "👋 Comment puis-je vous contacter ?",
  "💼 Parlez-moi de vos projets",
  "🚀 Quelles sont vos compétences ?",
  "📧 Comment collaborer ensemble ?",
  "📄 Puis-je voir votre CV ?"
];

const TypingIndicator = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="flex items-center space-x-2 p-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-xl w-fit"
  >
    <motion.div
      className="w-2 h-2 bg-indigo-600 rounded-full"
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
    />
    <motion.div
      className="w-2 h-2 bg-indigo-600 rounded-full"
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
    />
    <motion.div
      className="w-2 h-2 bg-indigo-600 rounded-full"
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
    />
  </motion.div>
);

const BotAvatar = () => (
  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center mr-2 flex-shrink-0 overflow-hidden">
    <img
      src="/images/omarBot.jpeg"
      alt="Omar Bot"
      className="w-full h-full object-cover"
    />
  </div>
);

interface MessageBubbleProps {
  message: Message;
  onConfirm: (response: 'oui' | 'non') => void;
}

const MessageBubble = ({ message, onConfirm }: MessageBubbleProps) => {
  const [hasResponded, setHasResponded] = useState(() => {
    // Récupérer l'état de réponse depuis le localStorage
    const messageId = message.timestamp.getTime().toString();
    const savedResponses = localStorage.getItem('chatResponses');
    if (savedResponses) {
      try {
        const responses: Record<string, boolean> = JSON.parse(savedResponses);
        return responses[messageId] || false;
      } catch (error) {
        console.error('Erreur lors de la lecture des réponses:', error);
        return false;
      }
    }
    return false;
  });

  const handleResponse = (response: 'oui' | 'non') => {
    if (!hasResponded) {
      setHasResponded(true);
      // Sauvegarder l'état de réponse dans le localStorage
      const messageId = message.timestamp.getTime().toString();
      const savedResponses = localStorage.getItem('chatResponses');
      let responses: Record<string, boolean> = {};
      if (savedResponses) {
        try {
          responses = JSON.parse(savedResponses);
        } catch (error) {
          console.error('Erreur lors de la lecture des réponses:', error);
        }
      }
      responses[messageId] = true;
      localStorage.setItem('chatResponses', JSON.stringify(responses));
      onConfirm(response);
    }
  };

  return (
    <div className={`flex ${message.isBot ? 'justify-start' : 'justify-end'} mb-4`}>
      {message.isBot && <BotAvatar />}
      <div
        className={`max-w-[80%] p-3 rounded-xl backdrop-blur-sm ${
          message.isBot
            ? 'bg-gray-100/80 dark:bg-gray-700/80 text-gray-900 dark:text-white border border-gray-300/50 dark:border-gray-600/50'
            : 'bg-indigo-600/90 text-white border border-indigo-500/50'
        }`}
      >
        <p className="text-sm whitespace-pre-line">{message.content}</p>
        {message.pendingAction && !hasResponded && (
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => handleResponse('oui')}
              className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            >
              Oui
            </button>
            <button
              onClick={() => handleResponse('non')}
              className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Non
            </button>
          </div>
        )}
        <span className="text-xs opacity-70 mt-1 block">
          {message.timestamp.toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
};

export const ChatBot = forwardRef<ChatBotRef>((_, ref) => {
  const [isOpen, setIsOpen] = useState(() => {
    // Si nous sommes sur la page d'accueil, forcer la fermeture
    if (window.location.pathname === '/') {
      localStorage.setItem('chatIsOpen', 'false');
      return false;
    }
    
    // Sinon, récupérer l'état d'ouverture depuis le localStorage
    const savedState = localStorage.getItem('chatIsOpen');
    if (savedState) {
      try {
        return JSON.parse(savedState);
      } catch (error) {
        console.error('Erreur lors de la lecture de l\'état du chat:', error);
        return false;
      }
    }
    return false;
  });

  // Fonction pour mettre à jour le timestamp de dernière activité
  const updateLastActivity = () => {
    localStorage.setItem('lastActivity', Date.now().toString());
  };

  // Fonction pour nettoyer le localStorage
  const clearLocalStorage = () => {
    localStorage.removeItem('chatHistory');
    localStorage.removeItem('chatResponses');
    localStorage.removeItem('chatSessionId');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('chatIsOpen');
    
    // Forcer la mise à jour de l'état
    setMessages([createWelcomeMessage()]);
    setSessionId(null);
    setIsOpen(false);
    setInputMessage('');
    setPendingAction(null);
  };

  // Vérifier périodiquement l'inactivité
  useEffect(() => {
    const checkInactivity = () => {
      const lastActivity = localStorage.getItem('lastActivity');
      if (lastActivity) {
        const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
        if (timeSinceLastActivity > 60000) { // 1 minute = 60000 ms
          clearLocalStorage();
        }
      }
    };

    // Vérifier toutes les 30 secondes
    const interval = setInterval(checkInactivity, 30000);
    return () => clearInterval(interval);
  }, []);

  // Synchroniser l'état avec le localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'chatHistory' && !e.newValue) {
        setMessages([createWelcomeMessage()]);
      }
      if (e.key === 'chatSessionId' && !e.newValue) {
        setSessionId(null);
      }
      if (e.key === 'chatIsOpen' && !e.newValue) {
        setIsOpen(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const [messages, setMessages] = useState<Message[]>(() => {
    // Récupérer l'historique depuis le localStorage au chargement
    const savedMessages = localStorage.getItem('chatHistory');
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        // Convertir les timestamps en objets Date
        return parsedMessages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      } catch (error) {
        console.error('Erreur lors de la lecture de l\'historique:', error);
      }
    }
    // Message initial si pas d'historique
    return [createWelcomeMessage()];
  });

  // Mettre à jour le timestamp de dernière activité à chaque interaction
  useEffect(() => {
    updateLastActivity();
  }, [messages, isOpen]);

  // Réinitialiser l'état du chat lors des changements de page
  useEffect(() => {
    const handleNavigation = () => {
      const currentPath = window.location.pathname;
      
      if (currentPath === '/' || currentPath === '') {
        // Réinitialiser complètement le chat
        clearLocalStorage();
        setIsOpen(false);
        setIsLowered(false);
        setMessages([createWelcomeMessage()]);
        setSessionId(null);
        setInputMessage('');
        setPendingAction(null);
      }
    };

    // Écouter les changements de navigation
    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('hashchange', handleNavigation);
    
    // Vérifier aussi au chargement initial
    handleNavigation();
    
    // Écouter les clics sur les liens
    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A' && (target as HTMLAnchorElement).getAttribute('href')?.startsWith('/')) {
        // Petit délai pour s'assurer que la navigation est terminée
        setTimeout(handleNavigation, 100);
      }
    };
    document.addEventListener('click', onDocumentClick);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('hashchange', handleNavigation);
      document.removeEventListener('click', onDocumentClick);
    };
  }, []);

  const [isLowered, setIsLowered] = useState(false);

  // Réinitialiser isLowered à chaque ouverture du chat
  useEffect(() => {
    if (isOpen) {
      setIsLowered(false);
    }
  }, [isOpen]);

  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(() => {
    // Récupérer le sessionId depuis le localStorage
    return localStorage.getItem('chatSessionId');
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [pendingAction, setPendingAction] = useState<{
    tool: string;
    params: any;
  } | null>(null);

  // Sauvegarder l'historique dans le localStorage à chaque changement
  useEffect(() => {
    // Garder les 2 dernières paires de messages (4 messages au total)
    const lastFourMessages = messages.slice(-4);
    localStorage.setItem('chatHistory', JSON.stringify(lastFourMessages));
  }, [messages]);

  // Récupérer l'historique depuis le localStorage au chargement
  useEffect(() => {
    const savedMessages = localStorage.getItem('chatHistory');
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        // Convertir les timestamps en objets Date
        const messagesWithDates = parsedMessages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        setMessages(messagesWithDates);
      } catch (error) {
        console.error('Erreur lors de la lecture de l\'historique:', error);
      }
    }
  }, []);

  // Sauvegarder le sessionId dans le localStorage
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('chatSessionId', sessionId);
    }
  }, [sessionId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  }));

  const handleQuickQuestion = (question: string) => {
    setInputMessage(question);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    updateLastActivity(); // Mettre à jour le timestamp avant d'envoyer le message

    const userMessage: Message = {
      content: inputMessage,
      isBot: false,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Si une action est en attente, vérifier la confirmation
      if (pendingAction) {
        const lowerText = inputMessage.toLowerCase();
        if (POSITIVE_CONFIRMATION_REGEX.test(lowerText)) {
          // Confirmation positive, exécuter l'action
          const actionResult = await executeAgentAction(pendingAction);
          if (actionResult.url) {
            if (pendingAction.tool === 'download_cv') {
              const link = document.createElement('a');
              link.href = actionResult.url;
              link.download = actionResult.filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } else {
              // Navigation fluide et cohérente avec handleConfirmation
              setIsLowered(true);
              setPendingAction(null);
              window.history.pushState({}, '', actionResult.url);
              window.dispatchEvent(new PopStateEvent('popstate'));
              return;
            }
          }
          setPendingAction(null);
        } else if (NEGATIVE_CONFIRMATION_REGEX.test(lowerText)) {
          // Confirmation négative
          const cancelMessage: Message = {
            content: "D'accord, pas de problème. Que puis-je faire d'autre pour vous ?",
            isBot: true,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, cancelMessage]);
          setPendingAction(null);
          setIsTyping(false);
          return;
        } else {
          // Réponse non claire, redemander confirmation
          const unclearMessage: Message = {
            content: "Je n'ai pas bien compris. Voulez-vous que je procède à cette action ?",
            isBot: true,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, unclearMessage]);
          setIsTyping(false);
          return;
        }
      }

      // Traitement via serveur proxy sécurisé
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? 'https://api.omarbelaid.fr/api/chat'  // Sous-domaine API en production
        : 'http://localhost:3001/api/chat';     // Serveur local en développement
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          message: userMessage.content, 
          sessionId,
          conversationHistory: messages,
        }),
      });
      
      if (!response.ok) throw new Error('Erreur réseau');

      const data = await response.json();
      
      if (data.sessionId) {
        setSessionId(data.sessionId);
      }

      if (!data || !data[0] || !data[0].generated_text) {
        console.error('Invalid response format:', data);
        throw new Error('Format de réponse invalide');
      }

      const botMessage: Message = {
        content: data[0].generated_text.trim(),
        isBot: true,
        timestamp: new Date(),
      };

      if (data[0].action) {
        botMessage.pendingAction = data[0].action;
        setPendingAction(data[0].action);
      }

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        content: "Je vous remercie de bien vouloir réessayer dans un petit instant 🙏.",
        isBot: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleConfirmation = async (response: 'oui' | 'non', pendingAction: any) => {
    if (response === 'oui') {
      const actionResult = await executeAgentAction(pendingAction);
      if (actionResult.url) {
        if (pendingAction.tool === 'download_cv') {
          const link = document.createElement('a');
          link.href = actionResult.url;
          link.download = actionResult.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          setIsLowered(true);
          // Réinitialiser uniquement l'action en attente
          setPendingAction(null);
          // Utiliser pushState pour une navigation fluide
          window.history.pushState({}, '', actionResult.url);
          // Déclencher un événement personnalisé pour notifier le changement d'URL
          window.dispatchEvent(new PopStateEvent('popstate'));
          return;
        }
      }
      setPendingAction(null);
    } else {
      const cancelMessage: Message = {
        content: "D'accord, pas de problème. Que puis-je faire d'autre pour vous ?",
        isBot: true,
        timestamp: new Date(),
      };
      setMessages((prev: Message[]) => [...prev, cancelMessage]);
      setPendingAction(null);
    }
  };

  const handleNavigation = () => {
    if (isOpen) {
      setIsLowered(true);
    }
  };

  const handleChatClick = () => {
    setIsLowered(false);
  };

  const [showQuickQuestions, setShowQuickQuestions] = useState(false);

  // Effet pour afficher les questions rapides après un délai
  useEffect(() => {
    if (messages.length === 1) {
      const timer = setTimeout(() => {
        setShowQuickQuestions(true);
      }, 20000); // Délai de 20 secondes

      return () => clearTimeout(timer);
    } else {
      setShowQuickQuestions(false);
    }
  }, [messages.length]);

  // Détecter les changements de page
  useEffect(() => {
    const handleNavigationEvent = () => {
      if (isOpen) {
        setIsLowered(true);
      }
    };

    // Écouter les changements de hash
    window.addEventListener('hashchange', handleNavigationEvent);
    // Écouter les changements d'historique
    window.addEventListener('popstate', handleNavigationEvent);
    // Écouter les clics sur les liens
    const onDocumentClickNav = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A' && (target as HTMLAnchorElement).getAttribute('href')?.startsWith('/')) {
        handleNavigationEvent();
      }
    };
    document.addEventListener('click', onDocumentClickNav);

    return () => {
      window.removeEventListener('hashchange', handleNavigationEvent);
      window.removeEventListener('popstate', handleNavigationEvent);
      document.removeEventListener('click', onDocumentClickNav);
    };
  }, [isOpen]);

  // Réinitialiser isLowered lors du changement de page si le chat est fermé
  useEffect(() => {
    const handlePageChange = () => {
      if (!isOpen) {
        setIsLowered(false);
      }
    };

    window.addEventListener('popstate', handlePageChange);
    window.addEventListener('hashchange', handlePageChange);

    return () => {
      window.removeEventListener('popstate', handlePageChange);
      window.removeEventListener('hashchange', handlePageChange);
    };
  }, [isOpen]);

  return (
    <>
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: isOpen ? 0 : 1 }}
        whileHover={{ scale: isOpen ? 0 : 1.1 }}
        className={`fixed bottom-16 right-4 bg-indigo-600 text-white p-4 rounded-full shadow-lg z-40 flex items-center justify-center ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
      >
        {createElement(FiMessageSquare as any, { className: 'w-6 h-6' })}
      </motion.button>

      <AnimatePresenceWrapper mode="wait">
        {isOpen && (
          <motion.div
            initial={false}
            animate={{ 
              y: isLowered ? 590 : 0
            }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 590 }}
            dragElastic={0.1}
            onDragEnd={(event, info) => {
              if (info.offset.y < -50 && isLowered) {
                setIsLowered(false);
              }
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30
            }}
            exit={{ opacity: 0, y: 100 }}
            className={`fixed bottom-16 right-4 w-96 h-[600px] chat-glass-effect rounded-xl shadow-2xl overflow-hidden z-50 cursor-pointer ${isLowered ? 'border-ultra-thin border-gray-300/50' : ''}`}
            onClick={handleChatClick}
            style={{
              touchAction: 'pan-y',
              userSelect: 'none',
            }}
          >
            {/* En-tête */}
            <div className="bg-indigo-600/90 dark:bg-indigo-700/90 backdrop-blur-md pt-1.5 pb-4 px-4 flex flex-col justify-between items-center border-b border-white/10 dark:border-gray-700/20">
              {/* Barre de glissement */}
              <div className="w-12 h-1 bg-white/20 rounded-full mb-2 cursor-grab active:cursor-grabbing" />
              
              <div className="flex justify-between items-center w-full">
                <div className="flex items-center">
                  <BotAvatar />
                  <h3 className="text-white font-medium">Chat avec Omar Bot</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearLocalStorage(); // Réinitialiser le chat
                      setIsOpen(false);
                      setIsLowered(false);
                      setMessages([{
                        content: "Bonjour ! Je suis Omar Bot. Que puis je faire pour vous ?",
                        isBot: true,
                        timestamp: new Date(),
                      }]);
                      setShowQuickQuestions(true);
                    }}
                    className="text-white hover:text-gray-200 transition-colors p-1"
                  >
                    {createElement(FiX as any, { className: 'w-6 h-6' })}
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="p-4 h-[calc(100%-8rem)] overflow-y-auto bg-gradient-to-b from-transparent to-white/10 dark:to-gray-900/10">
              <AnimatePresenceWrapper mode="popLayout">
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <MessageBubble 
                      message={message} 
                      onConfirm={(response) => handleConfirmation(response, message.pendingAction)} 
                    />
                  </motion.div>
                ))}
              </AnimatePresenceWrapper>

              {/* Questions rapides après les messages */}
              {messages.length === 1 && showQuickQuestions && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4"
                >
                  <div className="flex flex-wrap gap-2">
                    {quickQuestions.map((question, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleQuickQuestion(question)}
                        className="px-3 py-1 text-sm bg-gray-100/70 dark:bg-gray-700/70 backdrop-blur-sm border border-gray-300/50 dark:border-gray-600/50 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200/80 dark:hover:bg-gray-600/80 transition-all duration-200"
                      >
                        {question}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}

              <AnimatePresenceWrapper mode="wait">
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    className="flex justify-start mb-4"
                  >
                    <BotAvatar />
                    <TypingIndicator />
                  </motion.div>
                )}
              </AnimatePresenceWrapper>
              <div ref={messagesEndRef} />
            </div>

            {/* Zone de saisie */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-t border-gray-200/50 dark:border-gray-700/50">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className="flex gap-2"
              >
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Écrivez votre message..."
                  className="flex-1 p-2 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-gray-300/50 dark:border-gray-600/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
                <button
                  type="submit"
                  className="bg-indigo-600/90 backdrop-blur-sm text-white p-2 rounded-lg hover:bg-indigo-700/90 transition-all duration-200 flex items-center justify-center border border-indigo-500/50"
                  disabled={!inputMessage.trim()}
                >
                  {createElement(FiSend as any, { className: 'w-5 h-5' })}
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresenceWrapper>
    </>
  );
});